import type { Competitor, Phase, ProductRecord } from '../types.ts';

/**
 * Right-hand "Survey plan" panel: always shows the current record so the user sees what the
 * agent has built. `sessionId`/`runId` are optional so this stays usable without them; when
 * phase is `analysis` and a runId is known, it also carries a persistent artifact link
 * (reachability addendum, docs/specs/app.md v1.1 — this is the "state panel" half of
 * "state panel and/or header").
 */
export function StatePanel({
  product,
  competitors,
  phase,
  sessionId,
  runId,
}: {
  product: ProductRecord;
  competitors: Competitor[];
  phase: Phase;
  sessionId?: string;
  runId?: string | null;
}): JSX.Element {
  return (
    <aside className="state-panel">
      <div className="sp-section">
        <span className="label">Survey plan</span>
      </div>

      <div className="sp-section">
        <span className="label">Product</span>
        <h3 className="display sp-product-name">
          {product.name ?? <span className="muted">no name yet</span>}
        </h3>
        {product.pitch && <p className="sp-pitch">{product.pitch}</p>}
      </div>

      <div className="sp-section">
        <div className="sp-head">
          <span className="label">Capabilities</span>
          <span className="sp-count">{product.capabilities.length}</span>
        </div>
        {product.capabilities.length === 0 ? (
          <p className="muted">none yet</p>
        ) : (
          <ul className="sp-list">
            {product.capabilities.map((c, i) => (
              <li key={c.name} className="sp-cap">
                <span className="sp-idx">{String(i + 1).padStart(2, '0')}</span>
                <span className="sp-cap-body">
                  <span className="sp-cap-name">{c.name}</span>
                  <span className="sp-cap-desc">{c.description}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sp-section">
        <div className="sp-head">
          <span className="label">Competitors</span>
          <span className="sp-count">{competitors.length} / 6</span>
        </div>
        {competitors.length === 0 ? (
          <p className="muted">none yet</p>
        ) : (
          <ul className="sp-list">
            {competitors.map((c) => (
              <li key={c.name} className="sp-comp">
                <a href={c.landing_url} target="_blank" rel="noreferrer">
                  {c.name}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sp-section">
        <span className="label">Phase</span>
        <p className="sp-phase">{phase}</p>
        {phase === 'analysis' && sessionId && runId && (
          <a
            className="state-panel-artifact-link"
            href={`#/sessions/${sessionId}/runs/${runId}/artifact`}
          >
            View artifact →
          </a>
        )}
      </div>
    </aside>
  );
}
